import { useCallback, useEffect, useRef, useState } from "react";

// R15 U1 - ONE slot-selection rule for every booking surface (Customer 360, Calls, New consultation, reschedule, next-consultation, workflow outcome, public self-booking).
//
// The bug it replaces: each dialog kept `slot` in its own state and never cleared it when the date / type / consultant changed, so a slot chosen for 14 Oct stayed selected (and
// enabled "Confirm") after the date was changed to a closed 12 Oct, and was then submitted. Availability errors were also turned into an empty list, so "forbidden" or "network
// down" read as "closed / fully booked".
//
// Rules:
//  * A selection belongs to the exact request identity (`key`) it was made under. When the key changes the selection is invalid IMMEDIATELY (derived at render - there is no frame
//    in which the old slot is still submittable).
//  * A response is applied only if it is still the latest request for the current key (late / out-of-order responses are ignored).
//  * `slot` is non-null only when the status is "ready", the list was fetched for the current key, the slot is in that list, and its IST calendar date equals `date` (when given).
//  * The status says WHY there is nothing to pick: loading | empty (a real empty list) | forbidden (403) | error (network / server) - never folded into "empty".
//  * `reload()` refetches the same key (used after the server refuses a stale slot).

export const istDateOf = (iso) => {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
  } catch (e) {
    return null;
  }
};

export function useSlotPicker({ key, load, date = null }) {
  const [state, setState] = useState({ key: null, status: "idle", slots: null, error: null });
  const [selection, setSelection] = useState(null); // { key, start }
  const [nonce, setNonce] = useState(0);
  const latest = useRef(0);

  useEffect(() => {
    setSelection(null);       // a choice never outlives the request identity it was made under (so changing back to an earlier key does not resurrect it)
    if (!key) { setState({ key: null, status: "idle", slots: null, error: null }); return undefined; }
    const mine = ++latest.current;
    setState({ key, status: "loading", slots: null, error: null });
    Promise.resolve()
      .then(() => load())
      .then((slots) => {
        if (mine !== latest.current) return;
        const list = Array.isArray(slots) ? slots : [];
        setState({ key, status: list.length ? "ready" : "empty", slots: list, error: null });
      })
      .catch((e) => {
        if (mine !== latest.current) return;
        const code = e?.response?.status;
        setState({ key, status: code === 403 ? "forbidden" : "error", slots: null,
          error: e?.response?.data?.detail || e?.message || "Could not load availability" });
      });
    return () => { /* a newer key/nonce bumps latest.current, which discards this response */ };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);

  const settled = state.key === key && state.status === "ready" ? state.slots : null;
  const chosen = selection && selection.key === key ? selection.start : null;
  const valid = !!(settled && chosen && settled.some((s) => s.start === chosen) && (!date || istDateOf(chosen) === date));
  const slot = valid ? chosen : null;

  const select = useCallback((start) => {
    if (settled && settled.some((s) => s.start === start) && (!date || istDateOf(start) === date)) setSelection({ key, start });
  }, [settled, key, date]);

  const reload = useCallback(() => { setSelection(null); setNonce((n) => n + 1); }, []);
  const clear = useCallback(() => setSelection(null), []);

  // while the key is not the one the stored state belongs to, report "loading" rather than the previous key's answer
  const status = !key ? "idle" : state.key !== key ? "loading" : state.status;
  return { status, slots: state.key === key ? state.slots : null, error: state.key === key ? state.error : null, slot, select, reload, clear };
}

// A submit handler that ignores a second call while the first is still running (double click / Enter + click), independent of React's render timing.
export function useSingleFlight(fn) {
  const busy = useRef(false);
  const [running, setRunning] = useState(false);
  const run = useCallback(async (...args) => {
    if (busy.current) return undefined;
    busy.current = true;
    setRunning(true);
    try { return await fn(...args); } finally { busy.current = false; setRunning(false); }
  }, [fn]);
  return [run, running];
}

export const SLOT_STATUS_TEXT = {
  loading: "Checking availability…",
  empty: "No slots on this day — the office may be closed or every slot is taken.",
  forbidden: "You do not have permission to see availability here. This is not the same as “no slots”.",
  error: "Availability could not be loaded (network or server problem). This does not mean the day is full — try again.",
};
